import React, { useState, useEffect, useRef } from "react";

const DEFAULT_SCENES = [
  "Close your eyes and take a deep breath. Let your shoulders drop and your jaw unclench.",
  "Imagine a place where you feel completely at peace. What do you see around you?",
  "Notice the colors, the light, the sounds. You are safe here. You belong here.",
  "Take one more breath and carry this feeling with you as you slowly return.",
];
const DEFAULT_PACE = 12;

export default function VisualizationTemplate({ config = {}, onProgress, onComplete }) {
  const scenes = config.scenes?.length ? config.scenes : DEFAULT_SCENES;
  const pace   = config.pace || DEFAULT_PACE;

  const [index, setIndex]         = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const completedRef              = useRef(false);

  const done = index >= scenes.length;

  useEffect(() => {
    onProgress?.(index / scenes.length, `Scene ${index + 1} of ${scenes.length}`);
  }, [index]); // eslint-disable-line

  useEffect(() => {
    if (!isRunning || done) return;
    const timer = setTimeout(() => advanceScene(), pace * 1000);
    return () => clearTimeout(timer);
  }, [isRunning, index, done]); // eslint-disable-line

  const advanceScene = () => {
    if (fadingOut) return;
    setFadingOut(true);
    setTimeout(() => {
      const next = index + 1;
      setIndex(next);
      setFadingOut(false);
      if (next >= scenes.length) {
        setIsRunning(false);
        if (!completedRef.current) {
          completedRef.current = true;
          onComplete?.();
        }
      }
    }, 500);
  };

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-6 tw-text-center">
      {/* Ambient indicator bars */}
      <div className="tw-flex tw-items-center tw-gap-2">
        <span className="tw-text-[11px] tw-tracking-[2px] tw-uppercase" style={{ color: "rgba(255,255,255,0.35)" }}>
          Ambient
        </span>
        <div className="tw-flex tw-items-end tw-gap-[3px]" style={{ height: 16 }}>
          {[6, 12, 8, 14, 5].map((h, i) => (
            <div
              key={i}
              className="tw-rounded-sm"
              style={{
                width: 3,
                height: h,
                background: "rgba(203,90,60,0.5)",
                animation: `at-ambPulse 1.8s ease-in-out infinite ${(i * 0.1).toFixed(1)}s`,
              }}
            />
          ))}
        </div>
      </div>

      {/* Scene text with fade transition */}
      {!done && (
        <p
          className={fadingOut ? "at-scene-fade-out" : "at-scene-fade-in"}
          style={{
            fontSize: 24,
            fontWeight: 300,
            color: "rgba(255,255,255,0.88)",
            maxWidth: 360,
            lineHeight: 1.65,
          }}
        >
          {scenes[index]}
        </p>
      )}

      {/* Scene counter dots */}
      <div className="tw-flex tw-gap-2">
        {scenes.map((_, i) => (
          <div
            key={i}
            className="tw-rounded-full tw-transition-all tw-duration-300"
            style={{
              width: 7, height: 7,
              background:
                i < index  ? "rgba(203,90,60,0.5)" :
                i === index ? "#5B3FD6" :
                "rgba(255,255,255,0.18)",
            }}
          />
        ))}
      </div>

      {/* Controls */}
      <div className="tw-flex tw-items-center tw-gap-4">
        {!isRunning ? (
          <button
            onClick={() => setIsRunning(true)}
            className="tw-px-8 tw-py-2 tw-rounded-3xl tw-text-white tw-font-medium tw-transition-all hover:tw-scale-105"
            style={{ background: "#5B3FD6", border: "none" }}
          >
            Begin
          </button>
        ) : (
          <button
            onClick={advanceScene}
            className="tw-px-6 tw-py-2 tw-rounded-3xl tw-text-sm tw-transition-all hover:tw-scale-105"
            style={{
              background: "transparent",
              border: "1px solid rgba(160,140,255,0.35)",
              color: "rgba(160,140,255,0.85)",
            }}
          >
            Next scene
          </button>
        )}
      </div>
    </div>
  );
}
